import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  ListGroup,
  InputGroup,
  Offcanvas,
} from "react-bootstrap";
import { getApiClient } from "../libs/axiosInstance";
import { SUGGESTIONS } from "../libs/suggestions";
import * as LucideIcons from "lucide-react";
import Swal from "sweetalert2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { X } from "lucide-react";

// register once
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

/* ------------ Chart Coloring Helpers ------------ */
const BASE_COLORS = [
  "#2563eb",
  "#16a34a",
  "#db2777",
  "#f59e0b",
  "#6366f1",
  "#0891b2",
  "#f97316",
  "#059669",
  "#7c3aed",
  "#dc2626",
  "#0ea5e9",
  "#84cc16",
  "#9333ea",
  "#ef4444",
  "#14b8a6",
];
const withAlpha = (hex, alpha = 0.45) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(m[1], 16),
    g = parseInt(m[2], 16),
    b = parseInt(m[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
function colorizeDatasets(datasets) {
  return (datasets || []).map((ds, i) => {
    const c = BASE_COLORS[i % BASE_COLORS.length];
    return {
      ...ds,
      backgroundColor: withAlpha(c, 0.45),
      borderColor: c,
      borderWidth: 1.5,
    };
  });
}

function ChartJSRenderer({ graph }) {
  if (!graph?.data) return null;
  const data = {
    ...graph.data,
    datasets: colorizeDatasets(graph.data.datasets),
  };
  const options = graph.options || {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom" } },
  };
  return (
    <div style={{ width: "100%", height: 320 }}>
      <Bar data={data} options={options} />
    </div>
  );
}

/* ------------ Table + Legacy Line (kept) ------------ */
function TableRenderer({ table }) {
  if (!table?.columns || !table?.rows) return null;
  return (
    <div style={{ overflowX: "auto", marginTop: 8 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {table.columns.map((c, i) => (
              <th
                key={i}
                style={{
                  border: "1px solid #ddd",
                  padding: 6,
                  textAlign: "left",
                }}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={i}>
              {r.map((v, j) => (
                <td key={j} style={{ border: "1px solid #eee", padding: 6 }}>
                  {String(v)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LineGraph({ data, xKey, yKey, seriesName }) {
  if (!Array.isArray(data) || data.length === 0) return null;
  const w = 640,
    h = 240,
    pad = 30;
  const xs = data.map((d) => d[xKey]);
  const ys = data.map((d) => Number(d[yKey] || 0));
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const yRange = maxY - minY || 1;
  const stepX = (w - pad * 2) / (data.length - 1 || 1);
  const scaleY = (v) => h - pad - ((v - minY) / yRange) * (h - pad * 2);
  const path = ys
    .map((v, i) => `${i === 0 ? "M" : "L"} ${pad + i * stepX} ${scaleY(v)}`)
    .join(" ");
  return (
    <div style={{ overflowX: "auto", marginTop: 8 }}>
      <svg width={w} height={h} style={{ display: "block" }}>
        <rect x="0" y="0" width={w} height={h} fill="transparent" />
        <line
          x1={pad}
          y1={h - pad}
          x2={w - pad}
          y2={h - pad}
          stroke="currentColor"
        />
        <line x1={pad} y1={pad} x2={pad} y2={h - pad} stroke="currentColor" />
        <path d={path} fill="none" stroke="currentColor" />
        {xs.map((x, i) => (
          <text
            key={i}
            x={pad + i * stepX}
            y={h - 8}
            fontSize="10"
            textAnchor="middle"
          >
            {x}
          </text>
        ))}
        <text x={4} y={scaleY(minY)} fontSize="10">
          {minY}
        </text>
        <text x={4} y={scaleY(maxY)} fontSize="10">
          {maxY}
        </text>
        {seriesName ? (
          <text x={w / 2} y={14} fontSize="12" textAnchor="middle">
            {seriesName}
          </text>
        ) : null}
      </svg>
    </div>
  );
}

/* =================== MAIN =================== */
export default function AiChatRB() {
  const [handleAdd, setHandleAdd] = useState(false);
  const api = getApiClient();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null); // _id
  const [messages, setMessages] = useState([]); // [{role, content, ui?}]
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null); // controls ad slot
  const [search, setSearch] = useState(""); // chat filter
  const [forceShowChat, setForceShowChat] = useState(false); // hero -> chat before id
  const bottomRef = useRef(null);

  /* --------- helpers --------- */
  const filteredConvos = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return convos;
    return convos.filter((c) =>
      (c.label || "Untitled").toLowerCase().includes(q)
    );
  }, [convos, search]);

  const showHero = !active && !forceShowChat;
  const activeConvo = useMemo(
    () => convos.find((c) => c._id === active) || null,
    [convos, active]
  );

  /* --------- loaders --------- */
  const loadConvos = async () => {
    const { data } = await api.get("/api/ai/chat/convos");
    setConvos(data.convos || []);
  };
  const loadThread = async (id) => {
    const { data } = await api.get(`/api/ai/chat/convos/${id}`);
    const base = (data.messages || []).map((m) => ({
      role: m.role,
      content: m.content,
    }));
    setMessages(base);
  };

  useEffect(() => {
    loadConvos();
  }, []);
  useEffect(() => {
    if (active) loadThread(active);
  }, [active]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/ai/chat/usage");
        setPlan(data?.plan || "free");
      } catch {}
    })();
  }, []);

  /* --------- history actions --------- */
  const onNewConvo = async () => {
    const { data } = await api.post("/api/ai/chat/convos", {
      label: "New chat",
    });
    await loadConvos();
    setActive(data.convo._id);
    setForceShowChat(true);
  };

  const onRenameConvo = async (c) => {
    const submit = async (result) => {
      await api.patch(`/api/ai/chat/convos/${c._id}`, { label: result.value });
      await loadConvos();
      Swal.fire(
        "Successful",
        "Changed the chat name to " + result.value,
        "success"
      );
    };
    Swal.fire({
      title: "Rename Chat:",
      input: "text",
      inputPlaceholder: "Chat name here",
      showCancelButton: true,
      confirmButtonText: "Submit",
    }).then(async (result) => {
      if (result.isConfirmed) submit(result);
    });
  };

  const onDeleteConvo = async (c) => {
    const doDelete = async () => {
      await api.delete(`/api/ai/chat/convos/${c._id}`);
      await loadConvos();
      if (active === c._id) {
        setActive(null);
        setMessages([]);
        setForceShowChat(false);
      }
      Swal.fire("Successful", `Deleted chat named "${c.label}"`, "success");
    };
    Swal.fire({
      title: "Delete this chat?",
      showCancelButton: true,
      confirmButtonText: "Delete",
      icon: "warning",
    }).then(async (result) => {
      if (result.isConfirmed) doDelete();
    });
  };

  /* --------- sending --------- */
  // show chat immediately, seed with user + placeholder, disable inputs
  const enterChatLoadingView = (visibleText) => {
    setForceShowChat(true);
    setMessages([
      { role: "user", content: visibleText },
      { role: "assistant", content: "…thinking…" },
    ]);
  };

  const sendFirst = async (textOrObj) => {
    setLoading(true);
    try {
      const payload =
        typeof textOrObj === "string" ? textOrObj.trim() : textOrObj;
      const visible =
        typeof payload === "string" ? payload : payload.label || payload.msg;
      enterChatLoadingView(visible);

      const { data } = await api.post("/api/ai/chat/convos", {
        firstMessage: payload,
      });
      const newId = data?.convo?._id;
      await loadConvos();
      if (newId) setActive(newId);
      if (newId) await loadThread(newId);

      // attach any UI from custom-first-message to the last assistant
      if (data?.ui) {
        setMessages((m) => {
          const copy = [...m];
          for (let i = copy.length - 1; i >= 0; i--) {
            if (copy[i].role === "assistant") {
              copy[i] = { ...copy[i], ui: data.ui };
              break;
            }
          }
          return copy;
        });
      }
    } catch (e) {
      Swal.fire("Error", e?.response?.data?.error || "Failed to send", "error");
      setForceShowChat(false);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const sendToActive = async (payload) => {
    if (!active) return;
    setLoading(true);
    try {
      const visible =
        typeof payload === "string" ? payload : payload.label || payload.msg;
      setMessages((m) => [
        ...m,
        { role: "user", content: visible },
        { role: "assistant", content: "…thinking…" },
      ]);

      const body = typeof payload === "string" ? { message: payload } : payload;
      const { data } = await api.post(
        `/api/ai/chat/convos/${active}/messages`,
        body
      );

      // replace placeholder with real assistant + UI
      setMessages((m) => {
        const copy = [...m];
        for (let i = copy.length - 1; i >= 0; i--) {
          if (
            copy[i].role === "assistant" &&
            copy[i].content === "…thinking…"
          ) {
            copy[i] = {
              role: "assistant",
              content: data?.message?.content || "(no response)",
              ui: data?.ui || null,
            };
            break;
          }
        }
        return copy;
      });
    } catch (e) {
      Swal.fire("Error", e?.response?.data?.error || "Failed to send", "error");
      // remove last placeholder
      setMessages((m) => {
        const copy = [...m];
        if (
          copy.length &&
          copy[copy.length - 1].role === "assistant" &&
          copy[copy.length - 1].content === "…thinking…"
        ) {
          copy.pop();
        }
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  const onSend = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    if (!active) return sendFirst(text);
    return sendToActive(text);
  };

  const onSuggestion = async (s) => {
    if (loading) return;
    const payload = s.value ?? (s.type === "text" ? s.text : s);
    if (!active) return sendFirst(payload);
    return sendToActive(payload);
  };

  /* ------------ render ------------ */
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Container fluid className="h-100">
        <Row className="g-0 h-100">
          {/* Sidebar */}
          <Col
            xs={12}
            md={4}
            lg={3}
            xl={3}
            className="ai_chat_bot"
            style={{
              borderRight: "1px solid #ddd",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* floating new chat button visible only when in chat */}
            <div style={{ padding: 10, gap: 8 }}>
              <Button
                style={{
                  background: "white",
                  color: "black",
                  border: 0,
                  padding: "10px 25px",
                  borderRadius: 30,
                }}
                onClick={() => {
                  if (!loading) {
                    setActive(null);
                    setForceShowChat(false);
                    setMessages([]);
                  }
                }}
                disabled={loading || !active}
              >
                New Chat
              </Button>
            </div>
            <Form.Text
              className="text-muted fw-bold"
              style={{ padding: "10px" }}
            >
              Chats History
            </Form.Text>

            {/* Search bar */}
            <div style={{ padding: "0 10px 8px 10px" }}>
              <InputGroup>
                <Form.Control
                  placeholder="Search chats…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  disabled={loading}
                />
                {!!search && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearch("")}
                    disabled={loading}
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    {React.createElement(LucideIcons["X"], { size: 16 })}
                  </Button>
                )}
              </InputGroup>
            </div>

            <div style={{ padding: 10, overflowY: "auto" }}>
              <ListGroup>
                {filteredConvos.length === 0 && (
                  <div>No conversations yet.</div>
                )}
                {filteredConvos.map((c) => (
                  <div
                    key={c._id}
                    onClick={() => !loading && setActive(c._id)}
                    className="history"
                    style={{
                      cursor: loading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      color: "black",
                      background: active === c._id ? "#ccc" : "white",
                      opacity: loading ? 0.7 : 1,
                    }}
                  >
                    <button
                      style={{
                        background: "transparent",
                        border: 0,
                        padding: "10px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.label || "Untitled"}
                    </button>
                    <span
                      style={{ display: "inline-flex", gap: 6, padding: "5px" }}
                    >
                      <Button
                        size="sm"
                        className="p-0"
                        style={{
                          background: "transparent",
                          color: "green",
                          border: "none",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRenameConvo(c);
                        }}
                        disabled={loading}
                      >
                        {React.createElement(LucideIcons["Edit"], { size: 18 })}
                      </Button>
                      <Button
                        size="sm"
                        className="p-0"
                        style={{
                          background: "transparent",
                          color: "red",
                          border: "none",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteConvo(c);
                        }}
                        disabled={loading}
                      >
                        {React.createElement(
                          LucideIcons["Trash2"] || LucideIcons["Trash"],
                          { size: 18 }
                        )}
                      </Button>
                    </span>
                  </div>
                ))}
              </ListGroup>
            </div>
          </Col>

          {/* Main area */}
          <Col
            xs={12}
            md={12}
            lg={9}
            xl={9}
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              maxHeight: "80vh",
            }}
          >
            {/* HERO */}
            {showHero ? (
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  marginTop: "10vh",
                  justifyContent: "center",
                  padding: 16,
                }}
              >
                <div style={{ width: "100%", maxWidth: 720 }}>
                  <div
                    style={{
                      textAlign: "center",
                      marginBottom: 16,
                      fontSize: 24,
                      fontWeight: 600,
                    }}
                  >
                    Hello! How can I help you today?
                  </div>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      onSend();
                    }}
                  >
                    <Form.Control
                      placeholder="Ask anything"
                      value={input}
                      disabled={loading}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          onSend();
                        }
                      }}
                      style={{ borderRadius: 25, padding: "12px 25px" }}
                    />
                  </Form>
                  <div className="mt-5 text-center">
                    {SUGGESTIONS.map((s, i) => (
                      <div key={i} style={{ display: "inline-block" }}>
                        <Button
                          className="me-2 mb-2 px-3 py-2"
                          size="sm"
                          style={{
                            whiteSpace: "nowrap",
                            background: "white",
                            color: "black",
                            borderRadius: 25,
                            borderColor: "transparent",
                          }}
                          onClick={() => onSuggestion(s)}
                          disabled={loading}
                        >
                          {s.label ?? (s.type === "text" ? s.text : s.msg)}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Chat header */}
                <div
                  style={{
                    padding: 12,
                    borderBottom: "1px solid #ddd",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    {activeConvo ? activeConvo.label || "Chat" : "Chat"}
                  </div>
                </div>

                {/* Messages */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    padding: 12,
                  }}
                >
                  {messages.length === 0 && (
                    <div style={{ opacity: 0.7 }}>
                      Start by typing a message.
                    </div>
                  )}
                  {messages.map((m, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        marginBottom: 12,
                        alignSelf:
                          m.role === "user" ? "flex-end" : "flex-start",
                      }}
                    >
                      <div
                        style={{
                          maxWidth: "100%",
                          padding: 8,
                          border: "1px solid #ddd",
                          borderRadius: 8,
                          background: m.role === "user" ? "#0d6efd" : "white",
                          color: m.role === "user" ? "white" : "black",
                          opacity: m.content === "…thinking…" ? 0.8 : 1,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            opacity: 0.6,
                            marginBottom: 4,
                          }}
                        >
                          {m.role === "user" ? "You" : "Assistant"}
                        </div>
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          {m.content}
                        </div>

                        {/* UI payload (custom replies) */}
                        {m.role === "assistant" && m.ui && (
                          <div style={{ marginTop: 8 }}>
                            {m.ui.useTable && (
                              <TableRenderer table={m.ui.table} />
                            )}
                            {m.ui.useGraph &&
                              m.ui.graph?.kind === "chartjs" &&
                              m.ui.graph?.type === "bar" && (
                                <ChartJSRenderer graph={m.ui.graph} />
                              )}
                            {m.ui.useGraph && m.ui.graph?.kind === "line" && (
                              <LineGraph
                                data={m.ui.graph.data || []}
                                xKey={m.ui.graph.xKey || "label"}
                                yKey={m.ui.graph.yKey || "value"}
                                seriesName={m.ui.graph.seriesName}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>

                {/* Bottom input + suggestions (disabled while loading) */}
                <div style={{ borderTop: "1px solid #ddd", padding: 10 }}>
                  <InputGroup>
                    <Form.Control
                      as="textarea"
                      placeholder="Type your message…"
                      value={input}
                      disabled={loading}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          onSend();
                        }
                      }}
                      style={{ minHeight: 60, resize: "vertical" }}
                    />
                    <Button onClick={onSend} disabled={loading}>
                      Send
                    </Button>
                  </InputGroup>
                  <div className="mt-2 text-center">
                    {SUGGESTIONS.map((s, i) => (
                      <div key={i} style={{ display: "inline-block" }}>
                        <Button
                          className="me-2 mb-2 px-2 py-1"
                          size="sm"
                          style={{
                            whiteSpace: "nowrap",
                            background: "white",
                            color: "black",
                            borderRadius: 25,
                            borderColor: "transparent",
                          }}
                          onClick={() => onSuggestion(s)}
                          disabled={loading}
                        >
                          {s.label ?? (s.type === "text" ? s.text : s.msg)}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </Col>
        </Row>
      </Container>
      <div
        className="floating_ai"
        style={{
          cursor: "pointer",
        }}
        onClick={() => setHandleAdd(true)}
      >
        <LucideIcons.Sparkle className="sparkle_svg" color="#fff" />
      </div>

      <Offcanvas
        className="add_item_offcanvas"
        show={handleAdd}
        onHide={() => setHandleAdd(false)}
        placement="start"
      >
        <Offcanvas.Header>
          <Offcanvas.Title className="add_item_header">
            <button
              style={{
                background: "#fff",
                borderRadius: "100%",
                border: "none",
                padding: "2px 6px 3px 6px",
                cursor: "pointer",
                float: "right",
              }}
              onClick={() => setHandleAdd(false)}
            >
              <X />
            </button>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body
          style={{
            // custom scroll transparent
            overflowY: "auto",
            scrollbarWidth: "thin",
          }}
        >
          <div
            style={{
              borderRight: "1px solid #ddd",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              width: "100%",
            }}
          >
            {/* floating new chat button visible only when in chat */}
            <div style={{ padding: 10, gap: 8 }}>
              <Button
                style={{
                  background: "white",
                  color: "black",
                  border: 0,
                  padding: "10px 25px",
                  borderRadius: 30,
                }}
                onClick={() => {
                  if (!loading) {
                    setActive(null);
                    setForceShowChat(false);
                    setMessages([]);
                  }
                }}
                disabled={loading || !active}
              >
                New Chat
              </Button>
            </div>
            <Form.Text
              className="text-muted fw-bold"
              style={{ padding: "10px" }}
            >
              Chats History
            </Form.Text>

            {/* Search bar */}
            <div style={{ padding: "0 10px 8px 10px" }}>
              <InputGroup>
                <Form.Control
                  placeholder="Search chats…"
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  disabled={loading}
                />
                {!!search && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearch("")}
                    disabled={loading}
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    {React.createElement(LucideIcons["X"], { size: 16 })}
                  </Button>
                )}
              </InputGroup>
            </div>

            <div style={{ padding: 10, overflowY: "auto" }}>
              <ListGroup>
                {filteredConvos.length === 0 && (
                  <div>No conversations yet.</div>
                )}
                {filteredConvos.map((c) => (
                  <div
                    key={c._id}
                    onClick={() => !loading && setActive(c._id)}
                    className="history"
                    style={{
                      cursor: loading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      color: "black",
                      background: active === c._id ? "#ccc" : "white",
                      opacity: loading ? 0.7 : 1,
                    }}
                  >
                    <button
                      style={{
                        background: "transparent",
                        border: 0,
                        padding: "10px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      onClick={() => {
                        setHandleAdd(false);
                      }}
                    >
                      {c.label || "Untitled"}
                    </button>
                    <span
                      style={{ display: "inline-flex", gap: 6, padding: "5px" }}
                    >
                      <Button
                        size="sm"
                        className="p-0"
                        style={{
                          background: "transparent",
                          color: "green",
                          border: "none",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRenameConvo(c);
                        }}
                        disabled={loading}
                      >
                        {React.createElement(LucideIcons["Edit"], { size: 18 })}
                      </Button>
                      <Button
                        size="sm"
                        className="p-0"
                        style={{
                          background: "transparent",
                          color: "red",
                          border: "none",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteConvo(c);
                        }}
                        disabled={loading}
                      >
                        {React.createElement(
                          LucideIcons["Trash2"] || LucideIcons["Trash"],
                          { size: 18 }
                        )}
                      </Button>
                    </span>
                  </div>
                ))}
              </ListGroup>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
      {/* Optional ad area (inside Wrapper, below content) */}
      {plan === "free" && (
        <div style={{ borderTop: "1px solid #ddd", padding: 8 }}>
          <div style={{ textAlign: "center" }}>
            Ad space — upgrade to remove
          </div>
        </div>
      )}
    </div>
  );
}
